<template>
	<div
		class="images-modal"
		@click.self="closeModal"
	>
		<div class="images-modal__container">
			<!-- Close Button -->
			<button
				class="close-btn"
				@click="closeModal"
			>
				<UiIcon
					name="close"
					width="24px"
					height="24px"
					color="var(--white)"
				/>
			</button>

			<!-- Main Image Area -->
			<div class="main-image-wrapper">
				<div
					class="loader-wrapper"
					v-if="isImageLoading"
				>
					<UiLoader
						:circle-width="3"
						:radius="20"
						color="var(--white)"
					/>
				</div>
				<UiImage
					:src="currentImage"
					alt="Main image"
					class="main-image"
					:class="{ 'main-image_loading': isImageLoading }"
					@load="onImageLoaded"
					@error="onImageError"
				/>
			</div>

			<!-- Image Counter -->
			<div class="image-counter">
				<TypographyKTypographyBody5 color="var(--white)">
					{{ currentImageIndex + 1 }} / {{ images.length }}
				</TypographyKTypographyBody5>
			</div>

			<!-- Preview Bottom Section -->
			<div class="preview-section">
				<button
					class="arrow-btn arrow-btn_left"
					@click="previousImage"
					:disabled="currentImageIndex === 0"
				>
					<UiIcon
						name="arrow"
						width="20px"
						height="20px"
						color="var(--white)"
					/>
				</button>

				<!-- Preview Carousel -->
				<div class="preview-carousel">
					<div
						class="preview-item"
						v-for="(image, index) in images"
						:key="index"
						@click="selectImage(index)"
						:class="{ 'preview-item_active': index === currentImageIndex }"
					>
						<UiImage
							:src="image.preview || image.url"
							:alt="`Preview ${index}`"
							class="preview-image"
						/>
					</div>
				</div>

				<button
					class="arrow-btn arrow-btn_right"
					@click="nextImage"
					:disabled="currentImageIndex === images.length - 1"
				>
					<UiIcon
						name="arrow"
						width="20px"
						height="20px"
						color="var(--white)"
					/>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	interface ImageItem {
		url: string;
		preview?: string;
	}

	const modelValue = defineModel('modelValue', { required: true, type: Boolean });
	const props = withDefaults(
		defineProps<{
			images: ImageItem[];
			initialIndex: number | null;
		}>(),
		{
			initialIndex: 0
		}
	);

	const emits = defineEmits(['close', 'imageChange']);

	const currentImageIndex = ref(props.initialIndex || 0);
	const isImageLoading = ref(true);

	const currentImage = computed(() => {
		return props.images[currentImageIndex.value]?.url;
	});

	const closeModal = () => {
		modelValue.value = false;
		emits('close');
	};

	const onImageLoaded = () => {
		isImageLoading.value = false;
	};

	const onImageError = () => {
		isImageLoading.value = false;
	};

	const selectImage = (index: number) => {
		if (currentImageIndex.value === index) {
			return;
		}
		isImageLoading.value = true;
		currentImageIndex.value = index;
		emits('imageChange', index);
	};

	const nextImage = () => {
		if (currentImageIndex.value < props.images.length - 1) {
			selectImage(currentImageIndex.value + 1);
		}
	};

	const previousImage = () => {
		if (currentImageIndex.value > 0) {
			selectImage(currentImageIndex.value - 1);
		}
	};

	// Keyboard navigation
	const handleKeyDown = (e: KeyboardEvent) => {
		if (!modelValue.value) return;

		if (e.key === 'ArrowRight') nextImage();
		if (e.key === 'ArrowLeft') previousImage();
		if (e.key === 'Escape') closeModal();
	};

	watch(
		() => modelValue.value,
		(newVal) => {
			if (newVal) {
				window.addEventListener('keydown', handleKeyDown);
			} else {
				window.removeEventListener('keydown', handleKeyDown);
			}
		}
	);

	onBeforeUnmount(() => {
		window.removeEventListener('keydown', handleKeyDown);
	});
</script>

<style scoped lang="scss">
	.images-modal {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.95);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 9999;
		padding: 20px;
		animation: fadeIn 0.2s ease-in-out;

		&__container {
			position: relative;
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			max-width: 900px;
			max-height: 100vh;
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes spinLoader {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.close-btn {
		position: absolute;
		top: 10px;
		right: 10px;
		background: rgba(255, 255, 255, 0.1);
		border: none;
		border-radius: 50%;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		z-index: 100;
		transition: background-color 0.2s ease;

		&:hover {
			background-color: rgba(255, 255, 255, 0.2);
		}
	}

	.main-image-wrapper {
		position: relative;
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 80px;
		overflow: hidden;

		.loader-wrapper {
			position: absolute;
			z-index: 10;
		}

		.main-image {
			max-width: 100%;
			max-height: 100%;
			object-fit: contain;
			animation: imageSlideIn 0.3s ease-in-out;

			&_loading {
				opacity: 0.3;
			}
		}
	}

	@keyframes imageSlideIn {
		from {
			opacity: 0;
			transform: scale(0.95);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}

	.image-counter {
		position: absolute;
		top: 60px;
		right: 20px;
		background-color: rgba(0, 0, 0, 0.5);
		padding: 8px 12px;
		border-radius: 8px;
		backdrop-filter: blur(10px);
		z-index: 50;
	}

	.preview-section {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 16px;
		background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
		min-height: 100px;
	}

	.arrow-btn {
		background: rgba(255, 255, 255, 0.15);
		border: 1px solid rgba(255, 255, 255, 0.3);
		border-radius: 8px;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.2s ease;
		flex-shrink: 0;

		&:hover:not(:disabled) {
			background-color: rgba(255, 255, 255, 0.25);
			border-color: rgba(255, 255, 255, 0.5);
		}

		&:disabled {
			opacity: 0.4;
			cursor: not-allowed;
		}

		&_left {
			rotate: 90deg;
		}

		&_right {
			rotate: -90deg;
		}
	}

	.preview-carousel {
		display: flex;
		gap: 10px;
		flex: 1;
		overflow-x: auto;
		overflow-y: hidden;
		padding: 4px 0;
		scroll-behavior: smooth;

		&::-webkit-scrollbar {
			height: 4px;
		}

		&::-webkit-scrollbar-track {
			background: rgba(255, 255, 255, 0.1);
			border-radius: 4px;
		}

		&::-webkit-scrollbar-thumb {
			background: rgba(255, 255, 255, 0.3);
			border-radius: 4px;

			&:hover {
				background: rgba(255, 255, 255, 0.5);
			}
		}
	}

	.preview-item {
		position: relative;
		min-width: 70px;
		width: 70px;
		height: 70px;
		border-radius: 8px;
		cursor: pointer;
		overflow: hidden;
		border: 2px solid transparent;
		transition: all 0.2s ease;

		&:hover {
			border-color: rgba(255, 255, 255, 0.4);
		}

		&_active {
			border-color: var(--white);
			box-shadow: 0 0 6px rgba(255, 255, 255, 0.5);

			&::after {
				content: '';
				position: absolute;
				top: 0;
				left: 0;
				right: 0;
				bottom: 0;
				background-color: rgba(255, 255, 255, 0.1);
				pointer-events: none;
			}
		}

		.preview-image {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	/* Responsive */
	@media (max-width: 768px) {
		.images-modal {
			padding: 10px;

			&__container {
				max-height: 100vh;
			}
		}

		.main-image-wrapper {
			margin-bottom: 90px;
		}

		.preview-section {
			min-height: 90px;
			padding: 12px;
			gap: 8px;
		}

		.arrow-btn {
			width: 36px;
			height: 36px;
		}

		.preview-item {
			min-width: 60px;
			width: 60px;
			height: 60px;
		}

		.image-counter {
			top: 50px;
			right: 10px;
			font-size: 12px;
		}

		.close-btn {
			width: 36px;
			height: 36px;
			top: 5px;
			right: 5px;
		}
	}
</style>
